import React from 'react';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/loading';
import ModalErrorMessage from '../../components/modalErrorMessage/modalErrorMessage';
import PlanUpgradeDialog from '../../components/planUpgradeDialog/planUpgradeDialog';
import { useSaveProfessional } from './hooks/useSaveProfessional';
import { useProfessionalDigitalCertificate } from './hooks/useProfessionalDigitalCertificate';
import { useRooms } from '../../hooks/useRooms';
import { useRoomLabel } from '../../hooks/useRoomLabel';
import { hasPlanAtLeast, useTenantSubscription } from '../../hooks/useTenantSubscription';
import { WEEK_DAYS } from './workingHours';

const BRAZILIAN_UFS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

const CERTIFICATE_EXTENSIONS = ['.pfx', '.p12'];
const CERTIFICATE_MAX_SIZE_IN_BYTES = 1024 * 1024;

const maskCpf = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

const buildEmptyWorkingHours = () =>
  WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = { active: false, intervals: [] };
    return acc;
  }, {});

function ProfessionalModal({ open, onClose, onSave, professional: selectedProfessional }) {
  const navigate = useNavigate();
  const { save: saveProfessionalRequest } = useSaveProfessional();
  const { upload: uploadCertificateRequest, remove: removeCertificateRequest } =
    useProfessionalDigitalCertificate();
  const { fetchSubscription } = useTenantSubscription();
  const certificateInputRef = useRef(null);
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [cpf, setCpf] = useState('');
  const [roomId, setRoomId] = useState('');
  const [croNumber, setCroNumber] = useState('');
  const [croUf, setCroUf] = useState('');
  const [workingHours, setWorkingHours] = useState(buildEmptyWorkingHours());
  const [professionalId, setProfessionalId] = useState(null);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [removeCertificateModal, setRemoveCertificateModal] = useState(false);
  // undefined = not loaded yet, null = clinic without subscription.
  const [subscription, setSubscription] = useState(undefined);
  const [planUpgradeModal, setPlanUpgradeModal] = useState(false);
  // Response to hand to onSave when the modal closes without a full save (e.g. certificate removed),
  // so the parent refreshes its data.
  const [pendingRefresh, setPendingRefresh] = useState(null);

  // Loaded when the modal opens, so the certificate button can open the file picker right away
  // (browsers only allow it inside the click; waiting for a request first may get it blocked).
  useEffect(() => {
    if (!open) return;
    setSubscription(undefined);
    setPlanUpgradeModal(false);
    fetchSubscription()
      .then(setSubscription)
      .catch(() => setSubscription(undefined));
  }, [open, fetchSubscription]);

  useEffect(() => {
    if (open) {
      setProfessionalId(selectedProfessional?.id ?? null);
      setCurrentCertificate(
        selectedProfessional?.digitalCertificateFileName
          ? {
              fileName: selectedProfessional.digitalCertificateFileName,
              uploadedAt: selectedProfessional.digitalCertificateUploadedAt,
            }
          : null,
      );
      setCertificateFile(null);
      setPendingRefresh(null);
      setName(selectedProfessional?.name ?? '');
      setWhatsapp(selectedProfessional?.whatsApp ?? '');
      setCpf(selectedProfessional?.cpf ?? '');
      setRoomId(selectedProfessional?.roomId ?? '');
      setCroNumber(selectedProfessional?.croNumber ?? '');
      setCroUf(selectedProfessional?.croUf ?? '');
      setWorkingHours(loadWorkingHours(selectedProfessional?.workingHours));
      setShowErrorMessage(false);
      setErrorMessage('');
    }
  }, [selectedProfessional, open]);

  function loadWorkingHours(saved) {
    const loaded = buildEmptyWorkingHours();
    (saved ?? []).forEach((day) => {
      if (loaded[day.dayOfWeek]) {
        loaded[day.dayOfWeek] = {
          active: true,
          intervals: (day.intervals ?? []).map((interval) => ({
            start: interval.start ? dayjs(interval.start, 'HH:mm') : null,
            end: interval.end ? dayjs(interval.end, 'HH:mm') : null,
          })),
        };
      }
    });
    return loaded;
  }

  const toggleDay = (key) => {
    setWorkingHours((prev) => {
      const day = prev[key];
      const active = !day.active;
      return {
        ...prev,
        [key]: {
          active,
          intervals:
            active && day.intervals.length === 0
              ? [{ start: dayjs('08:00', 'HH:mm'), end: dayjs('18:00', 'HH:mm') }]
              : day.intervals,
        },
      };
    });
  };

  const addInterval = (key) => {
    setWorkingHours((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        intervals: [...prev[key].intervals, { start: null, end: null }],
      },
    }));
  };

  const removeInterval = (key, index) => {
    setWorkingHours((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        intervals: prev[key].intervals.filter((_, i) => i !== index),
      },
    }));
  };

  const updateInterval = (key, index, field, value) => {
    setWorkingHours((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        intervals: prev[key].intervals.map((interval, i) =>
          i === index ? { ...interval, [field]: value } : interval,
        ),
      },
    }));
  };

  const buildWorkingHoursPayload = () =>
    WEEK_DAYS.filter((day) => workingHours[day.key]?.active).map((day) => ({
      dayOfWeek: day.key,
      intervals: workingHours[day.key].intervals.map((interval) => ({
        start: interval.start ? dayjs(interval.start).format('HH:mm') : null,
        end: interval.end ? dayjs(interval.end).format('HH:mm') : null,
      })),
    }));

  // The digital certificate is a PLUS feature: BASIC (or no subscription) never reaches the file picker.
  const openCertificatePicker = async () => {
    let currentSubscription = subscription;
    if (currentSubscription === undefined) {
      setLoading(true);
      try {
        currentSubscription = await fetchSubscription();
        setSubscription(currentSubscription);
      } catch {
        setShowErrorMessage(true);
        setErrorMessage('Não foi possível verificar o plano da clínica. Tente novamente.');
        return;
      } finally {
        setLoading(false);
      }
    }
    if (!hasPlanAtLeast(currentSubscription, 'PLUS')) {
      setPlanUpgradeModal(true);
      return;
    }
    certificateInputRef.current?.click();
  };

  const handleSelectCertificate = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const lowerCaseName = file.name.toLowerCase();
    if (!CERTIFICATE_EXTENSIONS.some((extension) => lowerCaseName.endsWith(extension))) {
      setShowErrorMessage(true);
      setErrorMessage('Selecione um certificado digital A1 válido (arquivo .pfx ou .p12).');
      return;
    }
    if (file.size > CERTIFICATE_MAX_SIZE_IN_BYTES) {
      setShowErrorMessage(true);
      setErrorMessage('O certificado digital deve ter no máximo 1 MB.');
      return;
    }
    setShowErrorMessage(false);
    setCertificateFile(file);
  };

  const handleConfirmRemoveCertificate = () => {
    setLoading(true);
    removeCertificateRequest(professionalId)
      .then((returnFromApi) => {
        setCurrentCertificate(null);
        setRemoveCertificateModal(false);
        setPendingRefresh(returnFromApi);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao remover o certificado.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        setRemoveCertificateModal(false);
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    if (pendingRefresh) {
      onSave(pendingRefresh);
    }
    onClose();
  };

  // POST /dentista answers { id, message }; PUT answers only the message.
  const normalizeSaveResponse = (returnFromApi) => {
    const message = returnFromApi.data?.message ?? returnFromApi.data;
    return {
      professionalIdSaved: returnFromApi.data?.id ?? professionalId,
      response: { ...returnFromApi, data: message },
    };
  };

  const finishSave = (response) => {
    setLoading(false);
    cleanFields();
    onClose();
    onSave(response);
  };

  const saveProfessional = () => {
    if (!name.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('Inserir um nome é obrigatório.');
      return;
    }

    const input = {
      id: professionalId ?? undefined,
      nome: name,
      whatsApp: whatsapp,
      cpf: cpf.trim() || null,
      roomId: roomId || null,
      croNumber: croNumber.trim() || null,
      croUf: croUf || null,
      workingHours: buildWorkingHoursPayload(),
    };

    setLoading(true);
    saveProfessionalRequest(input)
      .then((returnFromApi) => {
        const { professionalIdSaved, response } = normalizeSaveResponse(returnFromApi);
        if (!certificateFile) {
          finishSave(response);
          return;
        }
        // Keep the id so a retry updates the professional instead of creating it again.
        setProfessionalId(professionalIdSaved);
        setPendingRefresh(response);
        uploadCertificateRequest(professionalIdSaved, certificateFile)
          .then(() => finishSave(response))
          .catch((returnFromApi) => {
            setLoading(false);
            setShowErrorMessage(true);
            setErrorMessage(
              'Profissional salvo, mas não foi possível enviar o certificado digital: ' +
                String(returnFromApi.response?.data ?? 'erro inesperado.'),
            );
            console.error('ops! ocorreu um erro' + returnFromApi);
            if (returnFromApi.status === 401) {
              navigate('/expired-session');
            }
          });
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao salvar o profissional.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  function cleanFields() {
    setName('');
    setWhatsapp('');
    setCpf('');
    setRoomId('');
    setCroNumber('');
    setCroUf('');
    setWorkingHours(buildEmptyWorkingHours());
    setProfessionalId(null);
    setCurrentCertificate(null);
    setCertificateFile(null);
    setPendingRefresh(null);
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {professionalId ? 'Editar Profissional' : 'Cadastrar Novo Profissional'}
      </DialogTitle>
      <DialogContent>
        <Loading isToHidden={isLoading === false} />
        {showErrorMessage && (
          <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 4,
            alignItems: 'start',
          }}
        >
          <Box>
            <TextField
              data-cy="dentist-name"
              id="name"
              label="Nome do profissional *"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mt: 2 }}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField
                id="whatsapp"
                label="WhatsApp"
                type="text"
                value={whatsapp}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const prevDigits = whatsapp.replace(/\D/g, '');
                  let digits = inputValue.replace(/\D/g, '').slice(0, 11);
                  if (digits === prevDigits && inputValue.length < whatsapp.length) {
                    digits = prevDigits.slice(0, -1);
                  }
                  let masked = '';
                  if (digits.length > 0) masked += '(' + digits.slice(0, 2);
                  if (digits.length >= 2) masked += ')' + digits.slice(2, 7);
                  if (digits.length >= 7) masked += '-' + digits.slice(7, 11);
                  setWhatsapp(masked);
                }}
                placeholder="(48)99999-9999"
                inputProps={{ maxLength: 14 }}
                sx={{ flex: 1 }}
              />
              <TextField
                id="cpf"
                label="CPF"
                type="text"
                value={cpf}
                onChange={(e) => setCpf(maskCpf(e.target.value))}
                placeholder="000.000.000-00"
                inputProps={{ inputMode: 'numeric', maxLength: 14 }}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField
                id="cro-number"
                label="Número do CRO"
                value={croNumber}
                onChange={(e) => setCroNumber(e.target.value.replace(/\D/g, '').slice(0, 20))}
                inputProps={{ inputMode: 'numeric' }}
                sx={{ flex: 1 }}
              />
              <TextField
                id="cro-uf"
                select
                label="UF do CRO"
                value={croUf}
                onChange={(e) => setCroUf(e.target.value)}
                sx={{ width: 120 }}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {BRAZILIAN_UFS.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              id="room"
              select
              label={roomLabel.singular}
              fullWidth
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              sx={{ mt: 2 }}
            >
              <MenuItem value="">
                <em>Nenhuma</em>
              </MenuItem>
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.name}
                </MenuItem>
              ))}
            </TextField>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: 3, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Certificado digital
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Envie o certificado digital A1 do profissional (arquivo .pfx ou .p12). Para assinar
              atestados, informe também o número e a UF do CRO.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />

            <input
              ref={certificateInputRef}
              type="file"
              accept=".pfx,.p12,application/x-pkcs12"
              hidden
              onChange={handleSelectCertificate}
            />
            {currentCertificate && !certificateFile && (
              <Stack direction="row" spacing={1} alignItems="center">
                <VerifiedUserOutlinedIcon color="success" fontSize="small" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {currentCertificate.fileName}
                  </Typography>
                  {currentCertificate.uploadedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Enviado em {currentCertificate.uploadedAt}
                    </Typography>
                  )}
                </Box>
                <Button size="small" onClick={openCertificatePicker}>
                  Substituir
                </Button>
                <IconButton
                  aria-label="remover certificado digital"
                  size="small"
                  color="error"
                  onClick={() => setRemoveCertificateModal(true)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            {certificateFile && (
              <Stack direction="row" spacing={1} alignItems="center">
                <UploadFileIcon color="primary" fontSize="small" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {certificateFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Será enviado ao salvar
                  </Typography>
                </Box>
                <IconButton
                  aria-label="descartar certificado selecionado"
                  size="small"
                  onClick={() => setCertificateFile(null)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            {!currentCertificate && !certificateFile && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={openCertificatePicker}
              >
                Selecionar certificado
              </Button>
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: { xs: 3, md: 2 }, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Horários de atendimento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Ao definir os horários de atendimento, o sistema saberá alertar e orientar na hora de
              marcar uma consulta, reduzindo erros de agendamento.
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <Stack divider={<Divider flexItem />} spacing={1.5}>
              {WEEK_DAYS.map((day) => {
                const dayState = workingHours[day.key];
                return (
                  <Box key={day.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={dayState.active}
                          onChange={() => toggleDay(day.key)}
                        />
                      }
                      label={day.label}
                    />

                    {dayState.active && (
                      <Stack spacing={1} sx={{ pl: 4, mt: 0.5 }}>
                        {dayState.intervals.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum horário adicionado.
                          </Typography>
                        )}
                        {dayState.intervals.map((interval, index) => (
                          <Stack key={index} direction="row" spacing={1} alignItems="center">
                            <TimeField
                              label="Início"
                              value={interval.start}
                              format="HH:mm"
                              onChange={(value) => updateInterval(day.key, index, 'start', value)}
                              sx={{ width: 110 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              às
                            </Typography>
                            <TimeField
                              label="Fim"
                              value={interval.end}
                              format="HH:mm"
                              onChange={(value) => updateInterval(day.key, index, 'end', value)}
                              sx={{ width: 110 }}
                            />
                            <IconButton
                              aria-label="remover horário"
                              size="small"
                              color="error"
                              onClick={() => removeInterval(day.key, index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}
                        <Box>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addInterval(day.key)}
                          >
                            Adicionar horário
                          </Button>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button data-cy="save-dentist-btn" onClick={saveProfessional} variant="contained">
          Salvar
        </Button>
      </DialogActions>

      <Dialog
        open={removeCertificateModal}
        onClose={() => setRemoveCertificateModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover certificado digital</DialogTitle>
        <DialogContent dividers>
          <Typography>
            O certificado digital será excluído e o profissional não poderá assinar documentos
            digitalmente até que um novo certificado seja enviado. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveCertificateModal(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmRemoveCertificate}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <PlanUpgradeDialog
        open={planUpgradeModal}
        onClose={() => setPlanUpgradeModal(false)}
        feature="A assinatura digital com certificado A1"
      />
    </Dialog>
  );
}

ProfessionalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  professional: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    whatsApp: PropTypes.string,
    cpf: PropTypes.string,
    roomId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    digitalCertificateFileName: PropTypes.string,
    croNumber: PropTypes.string,
    croUf: PropTypes.string,
    digitalCertificateUploadedAt: PropTypes.string,
    workingHours: PropTypes.arrayOf(
      PropTypes.shape({
        dayOfWeek: PropTypes.string,
        intervals: PropTypes.arrayOf(
          PropTypes.shape({
            start: PropTypes.string,
            end: PropTypes.string,
          }),
        ),
      }),
    ),
  }),
};

ProfessionalModal.defaultProps = {
  professional: null,
};

export default ProfessionalModal;
